import { Subject } from "rxjs";
import apiFactory from "..";
import MediaDevice from "./MediaDevice";

const PC_CONFIG = {
  iceServers: [
    { urls: ["stun:stun.l.google.com:19302"] },
    { urls: "stun:winitech.com.vn:3478" },
    {
      urls: "turn:winitech.com.vn:3478",
      username: "webrtcuser",
      credential: "webrtcpass",
    },
  ],
};

class PeerConnection extends Subject {
  constructor(friend, me, callingId, chatSocket, setCallingStatus) {
    super();
    this.chatSocket = chatSocket;
    this.friend = friend;
    this.me = me;
    this.callingId = callingId;
    this.setCallingStatus = setCallingStatus;
    this.pc = new RTCPeerConnection(PC_CONFIG);
    this.mediaDevice = new MediaDevice();

    this.audioTrack = null;
    this.videoTrack = null;

    this.pc.onicecandidate = this.sendCandidate;
    this.pc.ontrack = this.onTrack;
  }

  sendCandidate = (event) => {
    if (event.candidate) {
      this.chatSocket.send(
        JSON.stringify({
          event: "candidate",
          to: this.friend,
          candidate: event.candidate,
          from: this.me,
        })
      );
    }
  };

  onTrack = (event) => {
    this.next({
      event: "peerStream",
      stream: event.streams[0],
    });
  };

  async start(isCaller) {
    this.mediaDevice.subscribe(({ event, stream }) => {
      if (event === "stream") {
        this.next({ event: "localStream", stream: stream });
      }
    });

    if (isCaller) {
      try {
        let streamResult;

        if (this.me?.video) {
          await this.addAudio();
          streamResult = await this.addVideo();

          if (!streamResult) this.setCallingStatus("video");
        } else {
          streamResult = await this.addAudio();

          if (!streamResult) this.setCallingStatus("audio");
        }

        if (!streamResult) return;

        const result = await apiFactory.callingApi.sendRequest({
          event: "request",
          to: this.friend,
          from: this.me,
          callingId: this.callingId,
          sessionUuid: sessionStorage.getItem("sessionUuid"),
        });
        this.setCallingStatus(result?.data?.event);
      } catch (err) {
        console.error("Error sending call request:", err);
      }
    } else {
      if (this.me?.video) {
        await this.addAudio();
        await this.addVideo();
      } else {
        await this.addAudio();
      }

      await this.createOffer();
    }

    this.startMonitoringDevices();
    return this;
  }

  startMonitoringDevices(intervalMs = 3000) {
    let lastDeviceIds = null;

    const checkDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();

      const currentDeviceIds = devices
        .map((d) => d.deviceId)
        .sort()
        .join("|");

      if (currentDeviceIds !== lastDeviceIds) {
        lastDeviceIds = currentDeviceIds;

        let addedVideo = null;
        let addedAudio = null;

        if (
          this.me?.video &&
          (!this?.videoTrack || this?.videoTrack?.readyState === "ended")
        ) {
          addedVideo = await this.addVideo();
        }

        if (
          this.me?.audio &&
          (!this?.audioTrack || this?.audioTrack?.readyState === "ended")
        ) {
          addedAudio = await this.addAudio();
        }

        if (addedAudio || addedVideo) {
          await this.createOffer();
          addedAudio = null;
          addedVideo = null;
        }
      }
    };

    this._deviceInterval = setInterval(checkDevices, intervalMs);
  }

  async addAudio() {
    const v = await this.mediaDevice.getAudioStream();
    if (!v) return false;

    const audioTracks = this.mediaDevice.stream.getAudioTracks();
    if (audioTracks.length === 0) return false;

    // Nếu đã có track → xóa sender cũ trước khi add lại
    if (this.audioSender) {
      this.pc.removeTrack(this.audioSender);
      this.audioSender = null;
      this.audioTrack = null;
    }

    for (const track of audioTracks) {
      this.audioSender = this.pc.addTrack(track);
      this.audioTrack = track;
    }

    return true;
  }

  async addVideo() {
    const v = await this.mediaDevice.getVideoStream();
    if (!v) return false;

    const videoTracks = this.mediaDevice.stream.getVideoTracks();
    if (videoTracks.length === 0) return false;

    // ✅ Nếu đã có track → xóa sender cũ trước khi add lại
    if (this.videoSender) {
      this.pc.removeTrack(this.videoSender);
      this.videoSender = null;
      this.videoTrack = null;
    }

    for (const track of videoTracks) {
      this.videoSender = this.pc.addTrack(track, this.mediaDevice.stream);
      this.videoTrack = track;
    }

    return true;
  }

  removeAudio() {
    const senders = this.pc.getSenders();
    for (const sender of senders) {
      if (sender.track?.kind === "audio") {
        sender.track.stop();
        this.pc.removeTrack(sender);
      }
    }
    this.audioTrack = null;
    this.mediaDevice.stopAudio();
    return this;
  }

  removeVideo() {
    const senders = this.pc.getSenders();
    for (const sender of senders) {
      if (sender.track?.kind === "video") {
        sender.track.stop();
        this.pc.removeTrack(sender);
      }
    }
    this.videoTrack = null;
    this.mediaDevice.stopVideo();
    return this;
  }

  stop(isStarter) {
    if (isStarter) {
      this.chatSocket.send(
        JSON.stringify({
          event: "end",
          to: this.friend,
          from: this.me,
          callingId: this.callingId,
        })
      );
    }

    this.mediaDevice.stop();

    if (this.pc) {
      this.pc.getSenders().forEach((sender) => {
        if (sender.track) {
          sender.track.stop();
        }
      });
      this.pc.close();
      this.pc = null;
    }

    this.audioTrack = null;
    this.videoTrack = null;
    return this;
  }

  async createOffer() {
    try {
      const desc = await this.pc.createOffer();
      await this.setDescription(desc, "offer");
    } catch (err) {
      console.error("Failed to create offer:", err);
    }
    return this;
  }

  async createAnswer() {
    try {
      const desc = await this.pc.createAnswer();
      await this.setDescription(desc, "answer");
    } catch (err) {
      console.error("Failed to create answer:", err);
    }
    return this;
  }

  async setDescription(desc, type) {
    try {
      await this.pc.setLocalDescription(desc);
      this.chatSocket.send(
        JSON.stringify({
          event: type,
          to: this.friend,
          sdp: desc,
          from: this.me,
          callingId: this.callingId,
        })
      );
    } catch (err) {
      console.error(`Failed to set local description (${type}):`, err);
    }
    return this;
  }

  async setRemoteDescription(sdp) {
    try {
      const rtcSdp = new RTCSessionDescription(sdp);
      await this.pc.setRemoteDescription(rtcSdp);
    } catch (err) {
      console.error("Failed to set remote description:", err);
    }
    return this;
  }

  async addIceCandidate(candidate) {
    if (!candidate) return this;
    try {
      const iceCandidate = new RTCIceCandidate(candidate);
      await this.pc.addIceCandidate(iceCandidate);
    } catch (err) {
      console.error("Failed to add ICE candidate:", err);
    }
    return this;
  }

  toggleAudio(enabled) {
    if (this.audioTrack) {
      this.audioTrack.enabled = enabled;
    }
    return this;
  }

  async toggleVideo(enabled) {
    if (!enabled) {
      const senders = this.pc.getSenders();
      for (const sender of senders) {
        if (sender.track?.kind === "video") {
          sender.track.stop();
          this.pc.removeTrack(sender);
        }
      }
      this.videoTrack = null;
      this.mediaDevice.stopVideo();
      return this;
    } else {
      await this.addVideo();
      await this.createOffer();
    }
  }
}

export default PeerConnection;
